import { Text } from "@mantine/core";
import { IconFilePencil, IconPlayerPlay, IconTrash } from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import { ActionMenu } from "../../components/ActionMenu";
import type { FormDraft } from "../../hooks/useFormDrafts";

interface DraftActionMenuProps {
  draft: FormDraft;
  loading?: boolean;
  onResume: () => void;
  onDiscard: () => void;
}

/**
 * US-115 follow-up: the per-template "Draft" menu (Resume/Discard) shared by
 * FacilityFormsSection and StaffFormsSection — previously duplicated
 * near-verbatim across both. Pairs with useDraftResume for the fetch-on-resume
 * side. The resident-form equivalent (DraftFormMenu.tsx) predates this and
 * wasn't migrated — it owns its own ResidentFormFillModal wiring rather than
 * a bare onResume/onDiscard callback pair, a different enough shape that
 * folding it in wasn't worth touching stable, already-reviewed code for.
 */
export function DraftActionMenu({
  draft,
  loading,
  onResume,
  onDiscard,
}: DraftActionMenuProps) {
  const confirmDiscard = () =>
    modals.openConfirmModal({
      title: "Discard draft?",
      children: (
        <Text size="sm">
          This permanently deletes the saved draft for{" "}
          <strong>{draft.form_template_name ?? "this form"}</strong>. This can't
          be undone.
        </Text>
      ),
      labels: { confirm: "Discard", cancel: "Keep" },
      confirmProps: { color: "red" },
      onConfirm: onDiscard,
    });

  return (
    <ActionMenu
      label="Draft"
      color="yellow"
      loading={loading}
      leftSection={<IconFilePencil size={14} />}
      header={`Saved ${new Date(draft.updated_at).toLocaleString()}${draft.updated_by_name ? ` · ${draft.updated_by_name}` : ""}`}
      items={[
        {
          key: "resume",
          label: "Resume draft",
          leftSection: <IconPlayerPlay size={14} />,
          onClick: onResume,
        },
        {
          key: "discard",
          label: "Discard draft",
          leftSection: <IconTrash size={14} />,
          color: "red",
          onClick: confirmDiscard,
        },
      ]}
    />
  );
}
